import type { StorageValue, StorageKey, StorageResult, SavedDataInfo, StorageError } from './types';
import { STORAGE_KEYS } from './types';

// アプリが使うキーのみを許可するためのセット
const STORAGE_KEY_SET = new Set<StorageKey>(Object.values(STORAGE_KEYS));

const createError = (message: string): StorageError => ({
  success: false,
  error: message,
});

const successVoid = (): StorageResult<void> => ({
  success: true,
  data: undefined,
});

const ensureStorageAvailable = (): StorageResult<Storage> => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return createError('localStorage is not available (server-side environment)');
  }
  return {
    success: true,
    data: window.localStorage,
  };
};

const validateKey = (key: StorageKey): StorageResult<void> => {
  if (!STORAGE_KEY_SET.has(key)) {
    return createError(`Invalid storage key "${key}" is not allowed`);
  }
  return successVoid();
};

/**
 * localStorageへの安全な書き込み関数
 * @param key ストレージキー
 * @param value 保存する値
 * @returns 操作結果
 */
export function setStorageItem<T extends StorageValue>(
  key: StorageKey, 
  value: T
): StorageResult<void> {
  const keyValidation = validateKey(key);
  if (!keyValidation.success) {
    return keyValidation;
  }

  const storageResult = ensureStorageAvailable();
  if (!storageResult.success) {
    return storageResult;
  }

  try {
    if (value === undefined) {
      return {
        success: false,
        error: 'Cannot store undefined value in localStorage'
      };
    }

    const serializedValue = JSON.stringify(value);
    
    // ストレージサイズチェック（5MB制限を想定）
    if (serializedValue.length > 5 * 1024 * 1024) {
      return {
        success: false,
        error: 'Data size exceeds localStorage limit (5MB)'
      };
    }

    storageResult.data.setItem(key, serializedValue);
    
    return successVoid();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('localStorage setItem failed:', errorMessage);
    
    return {
      success: false,
      error: `Failed to save data: ${errorMessage}`
    };
  }
}

/**
 * localStorageからの安全な読み込み関数
 * @param key ストレージキー
 * @returns 操作結果と読み込まれたデータ
 */
export function getStorageItem<T extends StorageValue>(
  key: StorageKey
): StorageResult<T | null> {
  const keyValidation = validateKey(key);
  if (!keyValidation.success) {
    return keyValidation as StorageResult<T | null>;
  }

  const storageResult = ensureStorageAvailable();
  if (!storageResult.success) {
    return storageResult;
  }

  try {
    const item = storageResult.data.getItem(key);
    
    if (item === null) {
      return {
        success: true,
        data: null
      };
    }

    const parsedData = JSON.parse(item) as T;
    
    return {
      success: true,
      data: parsedData
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('localStorage getItem failed:', errorMessage);
    
    return {
      success: false,
      error: `Failed to load data: ${errorMessage}`
    };
  }
}

/**
 * localStorageからアイテムを削除する関数
 * @param key ストレージキー
 * @returns 操作結果
 */
export function removeStorageItem(key: StorageKey): StorageResult<void> {
  const keyValidation = validateKey(key);
  if (!keyValidation.success) {
    return keyValidation;
  }

  const storageResult = ensureStorageAvailable();
  if (!storageResult.success) {
    return storageResult;
  }

  try {
    storageResult.data.removeItem(key);
    return successVoid();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('localStorage removeItem failed:', errorMessage);
    
    return {
      success: false,
      error: `Failed to remove data: ${errorMessage}`
    };
  }
}

/**
 * 指定キーのデータ存在確認
 * @param key ストレージキー
 * @returns データの存在有無
 */
export function hasStorageItem(key: StorageKey): boolean {
  try {
    const storageResult = ensureStorageAvailable();
    if (!storageResult.success) {
      return false;
    }
    
    return storageResult.data.getItem(key) !== null;
  } catch (error) {
    console.error('localStorage hasItem failed:', error);
    return false;
  }
}

/**
 * 保存されたデータの情報を取得
 * @param key ストレージキー
 * @returns データ情報
 */
export function getStorageItemInfo(key: StorageKey): SavedDataInfo {
  try {
    const storageResult = ensureStorageAvailable();
    if (!storageResult.success) {
      return { timestamp: null, hasData: false };
    }

    const item = storageResult.data.getItem(key);
    
    if (item === null) {
      return { timestamp: null, hasData: false };
    }

    const size = new Blob([item]).size;
    
    try {
      const parsedData = JSON.parse(item);
      const timestamp = parsedData?.timestamp || null;
      
      return {
        timestamp,
        hasData: true,
        size
      };
    } catch {
      // timestampが含まれていない古いデータの場合
      return {
        timestamp: null,
        hasData: true,
        size
      };
    }
  } catch (error) {
    console.error('localStorage getItemInfo failed:', error);
    return { timestamp: null, hasData: false };
  }
}

/**
 * localStorageの全データを削除
 * @returns 操作結果
 */
export function clearAllStorage(): StorageResult<void> {
  const storageResult = ensureStorageAvailable();
  if (!storageResult.success) {
    return storageResult;
  }

  try {
    storageResult.data.clear();
    return successVoid();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('localStorage clear failed:', errorMessage);
    
    return {
      success: false,
      error: `Failed to clear storage: ${errorMessage}`
    };
  }
}

/**
 * このアプリケーション専用のキーのみを削除
 * @returns 操作結果
 */
export function clearAppStorage(): StorageResult<void> {
  const storageResult = ensureStorageAvailable();
  if (!storageResult.success) {
    return storageResult;
  }

  try {
    // アプリケーション専用のキーを削除
    Object.values(STORAGE_KEYS).forEach(key => {
      storageResult.data.removeItem(key);
    });
    
    return successVoid();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error('localStorage clearApp failed:', errorMessage);
    
    return {
      success: false,
      error: `Failed to clear app storage: ${errorMessage}`
    };
  }
}

/**
 * localStorageの使用可能容量を取得（概算）
 * @returns 使用可能かどうかと概算容量
 */
export function getStorageInfo(): { available: boolean; estimatedSize?: number } {
  try {
    const storageResult = ensureStorageAvailable();
    if (!storageResult.success) {
      return { available: false };
    }

    // テストデータで容量を計測
    const testKey = '__storage_test__';
    const testData = 'x'.repeat(1024); // 1KB
    
    storageResult.data.setItem(testKey, testData);
    storageResult.data.removeItem(testKey);
    
    // 既存データのサイズを概算
    let estimatedSize = 0;
    Object.values(STORAGE_KEYS).forEach(key => {
      const item = storageResult.data.getItem(key);
      if (item) {
        estimatedSize += new Blob([item]).size;
      }
    });
    
    return {
      available: true,
      estimatedSize
    };
  } catch (error) {
    console.error('Storage info check failed:', error);
    return { available: false };
  }
}
